// Centralized definitions to describe which upload slots belong to a given Minnesota
// question. Keys use canonical field IDs; callers can pass fallback qN ids which are
// automatically normalized via the alias map below.
interface AttachmentDefinition {
  slot: string;
  rawKey: string;
  fallbackLabel: string;
}

export interface MinnesotaAttachmentSlotConfig {
  slotKey: string;
  head?: string,
  label: string;
  docType: string;
  miscDocTypeKW: string;
}

export interface MinnesotaAttachmentSlotState {
  fileName?: string;
  isUploaded: boolean;
  uploading: boolean;
  deleting: boolean;
  error?: string;
}

export interface MinnesotaAttachmentStoredFile {
  id?: string;
  name: string;
  size?: number;
  type?: string;
  remoteKey?: string;
  docType?: string;
  miscDocTypeKW?: string;
  section?: string;
  isUploaded?: boolean;
}

// Updated structure: each slot holds an array of files instead of a single file.
export type MinnesotaAttachmentSlotMap = Record<string, MinnesotaAttachmentStoredFile[]>;
export type MinnesotaAttachmentFileMap = Record<string, MinnesotaAttachmentSlotMap>;

export interface StoredMinnesotaAttachment {
  questionId: string;
  slotKey: string;
  fileName: string;
  name?: string;
  id?: string;
  size?: number;
  type?: string;
  remoteKey?: string;
  docType?: string;
  miscDocTypeKW?: string;
  section?: string;
  isUploaded?: boolean;
}

const ATTACHMENT_DEFINITIONS: Record<string, AttachmentDefinition[]> = {
  q1metro619240: [
    { slot: 'writtenStatement', rawKey: 'upload1', fallbackLabel: 'Written Statement' },
    { slot: 'chargingDocument', rawKey: 'upload2', fallbackLabel: 'A certified copy of the charging document' },
    { slot: 'resolutionDocument', rawKey: 'upload3', fallbackLabel: 'Certified Resolution Document' }
  ],
  q2metro619240: [
    { slot: 'writtenStatement', rawKey: 'upload1', fallbackLabel: 'Written Statement' },
    { slot: 'noticeOfHearing', rawKey: 'upload2', fallbackLabel: 'Certified Notice of Hearing Document' },
    { slot: 'resolutionDocument', rawKey: 'upload3', fallbackLabel: 'Certified Resolution Document' }
  ],
  q3metro619240: [
    { slot: 'dischargeOfDebtor', rawKey: 'upload1', fallbackLabel: 'Discharge of Debtor' }
  ],
  q5metro619240: [
    { slot: 'writtenStatement', rawKey: 'upload1', fallbackLabel: 'Written Statement' },
    { slot: 'commencementDocument', rawKey: 'upload2', fallbackLabel: 'Lawsuit Commencement Document' },
    { slot: 'resolutionDocument', rawKey: 'upload3', fallbackLabel: 'Resolution / Final Judgment Document' }
  ],
  q7metro619240: [
    { slot: 'certificationOfLicensure', rawKey: 'upload1', fallbackLabel: 'Certification of Licensure' }
  ]
};

const QUESTION_ID_ALIASES: Record<string, string> = {
  q1: 'q1metro619240',
  q2: 'q2metro619240',
  q3: 'q3metro619240',
  q5: 'q5metro619240',
  q7: 'q7metro619240',
};

const asString = (value: unknown): string => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  return trimmed;
};

const resolveLabel = (rawUploadSection: unknown, fallbackLabel: string): string => {
  if (!rawUploadSection || typeof rawUploadSection !== 'object') return fallbackLabel;
  const section = rawUploadSection as Record<string, unknown>;
  const labelKeys = ['para1', 'title', 'label'];
  for (const key of labelKeys) {
    const candidate = asString(section[key]);
    if (candidate) return candidate;
  }
  return fallbackLabel;
};

const resolveDocMeta = (rawUploadSection: unknown): { docType: string; miscDocTypeKW: string } => {
  if (!rawUploadSection || typeof rawUploadSection !== 'object') {
    return { docType: '', miscDocTypeKW: '' };
  }
  const section = rawUploadSection as Record<string, unknown>;
  return {
    docType: asString(section.docType),
    miscDocTypeKW: asString(section.MiscDocTypeKW ?? section.miscDocTypeKW),
  };
};

const getCanonicalQuestionId = (questionId: string): string => {
  const trimmed = questionId?.trim();
  if (!trimmed) return questionId;
  return QUESTION_ID_ALIASES[trimmed] ?? trimmed;
};

export const deriveAttachmentConfigs = (
  questionId: string,
  raw?: Record<string, unknown>
): MinnesotaAttachmentSlotConfig[] => {
  const canonicalId = getCanonicalQuestionId(questionId);
  const definitions = ATTACHMENT_DEFINITIONS[canonicalId];
  if (!definitions?.length) return [];
  return definitions.map(({ slot, rawKey, fallbackLabel }) => {
    const rawSection = raw?.[rawKey];
    const head = (rawSection && typeof rawSection === 'object') ? asString((rawSection as Record<string, unknown>).head1) : '';
    const label = resolveLabel(rawSection, fallbackLabel);
    const meta = resolveDocMeta(rawSection);

    return {
      slotKey: slot,
      head,
      label,
      docType: meta.docType,
      miscDocTypeKW: meta.miscDocTypeKW,
    };
  });
};

export const listAttachmentQuestionIds = (): string[] => Object.keys(ATTACHMENT_DEFINITIONS);

const normalizeStoredFile = (
  questionId: unknown,
  slotKey: unknown,
  payload: unknown
): { qid?: string; slot?: string; file?: MinnesotaAttachmentStoredFile } => {
  const qid = asString(questionId);
  const slot = asString(slotKey);
  if (!qid || !slot) return {};

  if (!payload || (typeof payload !== 'object' && typeof payload !== 'string')) {
    const name = asString(payload);
    if (!name) return {};
    return { qid, slot, file: { name, isUploaded: true } };
  }

  if (typeof payload === 'string') {
    const name = payload.trim();
    if (!name) return {};
    return { qid, slot, file: { name, isUploaded: true } };
  }

  const record = payload as Record<string, unknown>;
  let name = asString(record.name ?? record.fileName ?? record.value ?? record.remoteFileName);
  if (!name) return {};
  
  // Extract filename from path (e.g., "450518/file-sample_150kB.pdf" → "file-sample_150kB.pdf")
  if (name.includes('/')) {
    const parts = name.split('/');
    name = parts[parts.length - 1]; // Get the last part after the last slash
  }
  
  const stored: MinnesotaAttachmentStoredFile = {
    name,
    id: asString(record.id ?? record.fileId),
    size: typeof record.size === 'number' ? record.size : undefined,
    type: asString(record.type ?? record.mimeType),
    remoteKey: asString(record.remoteKey ?? record.key),
    docType: asString(record.docType),
    miscDocTypeKW: asString(record.miscDocTypeKW ?? record.MiscDocTypeKW ?? record.docTypeKW),
    section: asString(record.section),
    isUploaded: record.isUploaded === undefined ? undefined : Boolean(record.isUploaded),
  };
  return { qid, slot, file: stored };
};

// Helper to map API files with docTypeKW to the correct question and slot
const mapApiFileToQuestionSlot = (
  apiFile: Record<string, unknown>
): { questionId?: string; slotKey?: string } => {
  const docType = asString(apiFile.docType);
  const docTypeKW = asString(apiFile.docTypeKW ?? apiFile.miscDocTypeKW);
  
  if (docType !== 'ICR - Miscellaneous' || !docTypeKW) {
    return {};
  }

  // Map specific docTypeKW values to question/slot combinations
  const docTypeKWMapping: Record<string, { questionId: string; slotKey: string }> = {
    'Question 1 Written Statement': { questionId: 'q1metro619240', slotKey: 'writtenStatement' },
    'Certified Charging Document': { questionId: 'q1metro619240', slotKey: 'chargingDocument' },
    'Q1 Certified Resolution Document': { questionId: 'q1metro619240', slotKey: 'resolutionDocument' },
    'Question 2 Written Statement': { questionId: 'q2metro619240', slotKey: 'writtenStatement' },
    'Certified Notice of Hearing': { questionId: 'q2metro619240', slotKey: 'noticeOfHearing' },
    'Q2 Certified Resolution Document': { questionId: 'q2metro619240', slotKey: 'resolutionDocument' },
    'Discharge of Debtor': { questionId: 'q3metro619240', slotKey: 'dischargeOfDebtor' },
    'Question 5 Written Statement': { questionId: 'q5metro619240', slotKey: 'writtenStatement' },
    'Lawsuit Commencement': { questionId: 'q5metro619240', slotKey: 'commencementDocument' },
    'Q5 Certified Resolution Document': { questionId: 'q5metro619240', slotKey: 'resolutionDocument' },
    'Certification of Licensure': { questionId: 'q7metro619240', slotKey: 'certificationOfLicensure' },
  };

  return docTypeKWMapping[docTypeKW] || {};
};

const applyMinnesotaEntry = (
  target: MinnesotaAttachmentFileMap,
  questionId: unknown,
  slotKey: unknown,
  payload: unknown
) => {
  const { qid, slot, file } = normalizeStoredFile(questionId, slotKey, payload);
  if (!qid || !slot || !file) return;
  if (!target[qid]) target[qid] = {};
  const arr = target[qid][slot];
  if (!arr) {
    target[qid][slot] = [file];
    return;
  }
  // Deduplicate: treat same remoteKey as match; if remoteKey missing on either, fall back to name match.
  const metaScore = (f: MinnesotaAttachmentStoredFile) => Number(Boolean(f.remoteKey)) + Number(Boolean(f.docType)) + Number(Boolean(f.size)) + Number(Boolean(f.isUploaded));
  const existingIndex = arr.findIndex(f => {
    if (f.remoteKey && file.remoteKey) return f.remoteKey === file.remoteKey;
    return f.name === file.name; // name match for pending vs uploaded version
  });
  if (existingIndex === -1) {
    arr.push(file);
  } else {
    const existing = arr[existingIndex];
    if (metaScore(file) >= metaScore(existing)) {
      arr[existingIndex] = file;
    }
  }
};

export const serializeMinnesotaFileMap = (
  map?: MinnesotaAttachmentFileMap,
  metaLookup?: (questionId: string, slotKey: string) => { docType?: string; miscDocTypeKW?: string; section?: string } | undefined
): StoredMinnesotaAttachment[] => {
  if (!map) return [];
  const out: StoredMinnesotaAttachment[] = [];
  Object.entries(map).forEach(([questionId, slots]) => {
    if (!slots || typeof slots !== 'object') return;
    Object.entries(slots).forEach(([slotKey, arr]) => {
      if (!Array.isArray(arr)) return;
      arr.forEach(stored => {
        if (!stored || typeof stored !== 'object') return;
        const name = asString(stored.name);
        if (!name) return;
        const normalized: MinnesotaAttachmentStoredFile = stored;
        const base: StoredMinnesotaAttachment = {
          questionId,
          slotKey,
          fileName: name,
          name,
          id: normalized.id,
          size: normalized.size,
            type: normalized.type,
          remoteKey: normalized.remoteKey,
          docType: normalized.docType,
          miscDocTypeKW: normalized.miscDocTypeKW,
          section: normalized.section,
          isUploaded: normalized.isUploaded,
        };
        if (metaLookup) {
          const meta = metaLookup(questionId, slotKey) || {};
          if (meta.docType && !base.docType) base.docType = meta.docType;
          if (meta.miscDocTypeKW && !base.miscDocTypeKW) base.miscDocTypeKW = meta.miscDocTypeKW;
          if (meta.section && !base.section) base.section = meta.section;
        }
        out.push(base);
      });
    });
  });
  return out;
};

export const buildMinnesotaFileMapFromStored = (src: unknown): MinnesotaAttachmentFileMap => {
  const out: MinnesotaAttachmentFileMap = {};
  if (!src) return out;

  // Helper to normalize a raw entry ensuring name derived from remoteFileName when absent
  const normalize = (raw: Record<string, unknown>): Record<string, unknown> => {
    if (raw.name) return raw;
    const remote = typeof raw.remoteFileName === 'string' ? raw.remoteFileName : undefined;
    if (remote) {
      const derived = remote.split('/').pop();
      if (derived && !raw.name) raw.name = derived;
    }
    return raw;
  };

  const ingestFlatArray = (items: unknown[]) => {
    items.forEach((item) => {
      if (!item || typeof item !== 'object') return;
      const anyItem = normalize(item as Record<string, unknown>);
      if (anyItem.docType === 'ICR - Miscellaneous' && anyItem.docTypeKW && !anyItem.questionId) {
        const mapping = mapApiFileToQuestionSlot(anyItem);
        if (mapping.questionId && mapping.slotKey) {
          applyMinnesotaEntry(out, mapping.questionId, mapping.slotKey, anyItem);
          return;
        }
      }
      applyMinnesotaEntry(
        out,
        anyItem.questionId ?? anyItem.question ?? anyItem.section ?? anyItem.id,
        anyItem.slotKey ?? anyItem.slot ?? anyItem.field ?? 'default',
        anyItem
      );
    });
  };

  const ingestQuestionArrayPayload = (questionId: string, payload: unknown[]) => {
    payload.forEach((entry) => {
      if (!entry || typeof entry !== 'object') return;
      const anyEntry = normalize(entry as Record<string, unknown>);
      applyMinnesotaEntry(
        out,
        questionId,
        anyEntry.slotKey ?? anyEntry.slot ?? anyEntry.field ?? 'default',
        anyEntry
      );
    });
  };

  const ingestSlotObjectPayload = (questionId: string, valueObj: Record<string, unknown>) => {
    Object.entries(valueObj).forEach(([slotKey, value]) => {
      if (Array.isArray(value)) {
        value.forEach(v => {
          if (!v || typeof v !== 'object') return;
          applyMinnesotaEntry(out, questionId, slotKey, normalize(v as Record<string, unknown>));
        });
        return;
      }
      applyMinnesotaEntry(out, questionId, slotKey, normalize(value as Record<string, unknown>));
    });
  };

  const ingestRecord = (record: Record<string, unknown>) => {
    Object.entries(record).forEach(([questionId, payload]) => {
      if (Array.isArray(payload)) {
        ingestQuestionArrayPayload(questionId, payload);
        return;
      }
      if (payload && typeof payload === 'object') {
        ingestSlotObjectPayload(questionId, payload as Record<string, unknown>);
        return;
      }
      if (typeof payload === 'string') {
        applyMinnesotaEntry(out, questionId, 'default', { name: payload, isUploaded: true });
      }
    });
  };

  if (Array.isArray(src)) {
    ingestFlatArray(src);
    return out;
  }
  if (typeof src === 'object') {
    ingestRecord(src as Record<string, unknown>);
  }
  return out;
};

export const mergeMinnesotaFileMaps = (
  ...maps: Array<MinnesotaAttachmentFileMap | undefined>
): MinnesotaAttachmentFileMap => {
  const merged: MinnesotaAttachmentFileMap = {};
  const ingestSlotEntries = (questionId: string, slotKey: string, value: unknown) => {
    if (Array.isArray(value)) {
      value.forEach(v => applyMinnesotaEntry(merged, questionId, slotKey, v));
      return;
    }
    applyMinnesotaEntry(merged, questionId, slotKey, value);
  };
  const ingestMap = (map: MinnesotaAttachmentFileMap | undefined) => {
    if (!map || typeof map !== 'object') return;
    Object.entries(map).forEach(([questionId, slots]) => {
      if (!slots || typeof slots !== 'object') return;
      Object.entries(slots).forEach(([slotKey, value]) => ingestSlotEntries(questionId, slotKey, value));
    });
  };
  maps.forEach(ingestMap);
  return merged;
};

// Helper function to enrich a single file
const enrichSingleFile = (
  file: MinnesotaAttachmentStoredFile,
  questionId: string,
  slotKey: string,
  definitions: AttachmentDefinition[] | undefined,
  metroRecord: Record<string, unknown>,
  miscDocTypeKWLookup: Map<string, { questionId: string; slotKey: string }>
): MinnesotaAttachmentStoredFile => {
  if (!file || typeof file !== 'object') {
    return file;
  }

  const enriched = { ...file };
  
  // For API files with ICR - Miscellaneous docType, map docTypeKW to miscDocTypeKW
  if (file.docType === 'ICR - Miscellaneous' && file.miscDocTypeKW) {
    const lookup = miscDocTypeKWLookup.get(file.miscDocTypeKW);
    if (lookup && lookup.questionId === questionId && lookup.slotKey === slotKey) {
      // File is already in correct position, keep its metadata
      return enriched;
    }
  }
  
  // Find matching definition for this slot
  const definition = definitions?.find(def => def.slot === slotKey);
  if (!definition) {
    return enriched;
  }
  
  // Get metro config for this upload section
  const page5Questions = metroRecord.Page5Questions as Record<string, unknown> | undefined;
  const uploadSection = page5Questions?.[definition.rawKey] as Record<string, unknown> | undefined;
  
  if (uploadSection) {
    const metaData = resolveDocMeta(uploadSection);
    enriched.docType = metaData.docType || enriched.docType;
    enriched.miscDocTypeKW = metaData.miscDocTypeKW || enriched.miscDocTypeKW;
  }
  
  return enriched;
};

/**
 * Enriches Minnesota attachment files with docType and miscDocTypeKW from metro config.
 * For files from API with docType "ICR - Miscellaneous", maps docTypeKW to correct miscDocTypeKW.
 */
export const enrichMinnesotaFilesWithMetroConfig = (
  fileMap: MinnesotaAttachmentFileMap,
  metroConfig?: unknown
): MinnesotaAttachmentFileMap => {
  if (!fileMap || typeof fileMap !== 'object' || !metroConfig || typeof metroConfig !== 'object') {
    return fileMap;
  }

  const enriched: MinnesotaAttachmentFileMap = {};
  const metroRecord = metroConfig as Record<string, unknown>;

  // Build lookup map of MiscDocTypeKW to questionId/slotKey for reverse mapping
  const miscDocTypeKWLookup = new Map<string, { questionId: string; slotKey: string }>();
  
  Object.entries(fileMap).forEach(([questionId, slots]) => {
    if (!slots || typeof slots !== 'object') {
      enriched[questionId] = slots;
      return;
    }

    const canonicalQuestionId = getCanonicalQuestionId(questionId);
    const definitions = ATTACHMENT_DEFINITIONS[canonicalQuestionId];
    
    // Build lookup for this question's upload sections
    if (definitions) {
      definitions.forEach(({ slot, rawKey }) => {
        const uploadSection = metroRecord[rawKey];
        if (uploadSection && typeof uploadSection === 'object') {
          const sectionRecord = uploadSection as Record<string, unknown>;
          const miscDocTypeKW = asString(sectionRecord.MiscDocTypeKW ?? sectionRecord.miscDocTypeKW);
          if (miscDocTypeKW) {
            miscDocTypeKWLookup.set(miscDocTypeKW, { questionId: canonicalQuestionId, slotKey: slot });
          }
        }
      });
    }
    
    const enrichedSlots: MinnesotaAttachmentSlotMap = {};
    
    Object.entries(slots).forEach(([slotKey, files]) => {
      if (Array.isArray(files)) {
        enrichedSlots[slotKey] = files.map(f => enrichSingleFile(f, questionId, slotKey, definitions, metroRecord, miscDocTypeKWLookup));
      } else {
        // Legacy single-file slot (convert to array)
        const single = enrichSingleFile(files as unknown as MinnesotaAttachmentStoredFile, questionId, slotKey, definitions, metroRecord, miscDocTypeKWLookup);
        enrichedSlots[slotKey] = [single];
      }
    });

    enriched[questionId] = enrichedSlots;
  });

  return enriched;
};
